/**
 * Promo Codes (admin) — create codes, switch them on/off, see who used them.
 *
 * All rules live on the server (functions/src/payments/promoShared.ts). The
 * browser cannot read or write `promoCodes` at all (default-deny in
 * firestore.rules), so everything here goes through three admin callables and
 * every change is audited + emailed.
 *
 *  - A code is a whole-number percent off (1-90). Never 100%, never an amount.
 *  - After creation only on/off (and the note) can change. To "edit" a code:
 *    switch it off and create a new one. A code string is never reused.
 *  - Standing client discount vs a code: the better one wins, never both.
 *
 * Opened with ?client=<id> (from Manage Client) it starts on "one client".
 */

import { useEffect, useMemo, useRef, useState, FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';
import { PortalHeader } from '../Layout/PortalHeader';

interface ClientOption { id: string; name: string; email: string }

interface PromoRow {
  code: string;
  status: 'active' | 'scheduled' | 'expired' | 'used_up' | 'off' | 'broken';
  kind: string;
  clientId: string | null;
  percentOff: number | null;
  scope: string;
  startsOn: string | null;
  endsOn: string | null;
  maxPerClient: number | null;
  maxTotalRedemptions: number | null;
  redeemedCount: number;
  openHolds: number;
  active: boolean;
  note: string;
  createdAtMs: number | null;
}

interface RedemptionRow {
  id: string;
  clientId: string;
  status: string;
  lapsed: boolean;
  tierId: string;
  listUsd: number | null;
  discountUsd: number | null;
  netUsd: number | null;
  reservedAtMs: number | null;
  redeemedAtMs: number | null;
  releasedReason: string | null;
  overRedeemed: boolean;
}

const STATUS_LABEL: Record<PromoRow['status'], string> = {
  active: 'Active', scheduled: 'Scheduled', expired: 'Expired', used_up: 'Used up', off: 'Off', broken: 'Broken record',
};
const STATUS_COLOR: Record<PromoRow['status'], string> = {
  active: '#10b981', scheduled: '#3b82f6', expired: 'var(--gray-500)', used_up: 'var(--gray-500)', off: '#dc2626', broken: '#dc2626',
};
const SCOPE_LABEL: Record<string, string> = { credits: 'Credit packs', platform: 'Platform fee', both: 'Credits + platform fee' };

const usd = (n: number | null | undefined) => (typeof n !== 'number' ? '-' : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const when = (ms: number | null) => (ms ? new Date(ms).toLocaleString('en-US', { timeZone: 'America/Chicago' }) : '-');
const errText = (err: unknown) => (err instanceof Error ? err.message : 'Something went wrong');

export function PromoCodes() {
  const [params] = useSearchParams();
  const presetClient = params.get('client') || '';

  const [clients, setClients] = useState<ClientOption[]>([]);
  const [codes, setCodes] = useState<PromoRow[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState('');

  // Create form
  const [kind, setKind] = useState<'shared' | 'single_client'>(presetClient ? 'single_client' : 'shared');
  const [clientId, setClientId] = useState(presetClient);
  const [percentOff, setPercentOff] = useState('');
  const [scope, setScope] = useState<'credits' | 'platform' | 'both'>('both');
  const [code, setCode] = useState('');
  const [startsOn, setStartsOn] = useState('');
  const [endsOn, setEndsOn] = useState('');
  const [maxPerClient, setMaxPerClient] = useState('1');
  const [maxTotal, setMaxTotal] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [created, setCreated] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Row actions
  const [busyCode, setBusyCode] = useState<string | null>(null);
  const [rowError, setRowError] = useState('');
  const [openCode, setOpenCode] = useState<string | null>(null);
  const [redemptions, setRedemptions] = useState<RedemptionRow[] | null>(null);
  // Only the LATEST "Uses" click may fill the panel (a slow answer for code A must not land under code B).
  const usesRequest = useRef(0);

  const clientName = useMemo(() => {
    const map = new Map(clients.map(c => [c.id, c.name || c.email || c.id]));
    return (id: string | null) => (id ? map.get(id) || id : '');
  }, [clients]);

  const loadCodes = async () => {
    setListLoading(true);
    setListError('');
    try {
      const fn = httpsCallable<Record<string, never>, { codes: PromoRow[] }>(functions, 'adminListPromoCodes');
      const res = await fn({});
      setCodes(res.data.codes || []);
    } catch (err) {
      setListError(errText(err));
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    loadCodes();
    (async () => {
      try {
        const fn = httpsCallable<Record<string, never>, { clients: ClientOption[] }>(functions, 'listClients');
        const res = await fn({});
        setClients((res.data.clients || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      } catch (err) {
        console.error('[PromoCodes] could not load clients', err);
      }
    })();
  }, []);

  const wholeNumber = (v: string): number | null => (/^\d+$/.test(v.trim()) ? parseInt(v.trim(), 10) : null);

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    setFormError('');
    setCreated(null);
    setCopied(false);

    const pct = wholeNumber(percentOff);
    if (pct === null || pct < 1 || pct > 90) { setFormError('Percent off must be a whole number from 1 to 90.'); return; }
    if (kind === 'single_client' && !clientId) { setFormError('Pick the client this code is for.'); return; }
    if (kind === 'shared' && !endsOn) { setFormError('A shared code needs an end date.'); return; }

    const payload: Record<string, unknown> = { kind, percentOff: pct, scope, note: note.trim() };
    if (code.trim()) payload.code = code.trim();
    if (startsOn) payload.startsOn = startsOn;
    if (endsOn) payload.endsOn = endsOn;
    if (kind === 'single_client') {
      payload.clientId = clientId;
    } else {
      const per = wholeNumber(maxPerClient);
      if (per === null || per < 1) { setFormError('Uses per client must be a whole number, 1 or more.'); return; }
      payload.maxPerClient = per;
      if (maxTotal.trim()) {
        const total = wholeNumber(maxTotal);
        if (total === null || total < 1) { setFormError('Total uses must be a whole number, or blank for no cap.'); return; }
        payload.maxTotalRedemptions = total;
      }
    }

    setSaving(true);
    try {
      const fn = httpsCallable<Record<string, unknown>, { code: string }>(functions, 'adminCreatePromoCode');
      const res = await fn(payload);
      setCreated(res.data.code);
      setCode(''); setPercentOff(''); setNote(''); setStartsOn(''); setEndsOn(''); setMaxTotal(''); setMaxPerClient('1');
      await loadCodes();
    } catch (err) {
      setFormError(errText(err));
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (row: PromoRow) => {
    setBusyCode(row.code);
    setRowError('');
    try {
      const fn = httpsCallable(functions, 'adminSetPromoCodeActive');
      await fn({ code: row.code, active: !row.active });
      await loadCodes();
    } catch (err) {
      setRowError(`${row.code}: ${errText(err)}`);
    } finally {
      setBusyCode(null);
    }
  };

  const handleViewUses = async (row: PromoRow) => {
    const mine = ++usesRequest.current;
    if (openCode === row.code) { setOpenCode(null); setRedemptions(null); return; }
    setOpenCode(row.code);
    setRedemptions(null);
    setRowError('');
    try {
      const fn = httpsCallable<{ redemptionsFor: string }, { redemptions: RedemptionRow[] }>(functions, 'adminListPromoCodes');
      const res = await fn({ redemptionsFor: row.code });
      if (mine !== usesRequest.current) return;
      setRedemptions(res.data.redemptions || []);
    } catch (err) {
      if (mine !== usesRequest.current) return;
      setRowError(`${row.code}: ${errText(err)}`);
      setOpenCode(null);
    }
  };

  const copyCreated = async () => {
    if (!created) return;
    try { await navigator.clipboard.writeText(created); setCopied(true); } catch { setCopied(false); }
  };

  const usedText = (r: PromoRow) =>
    `${r.redeemedCount} of ${r.maxTotalRedemptions ?? 'no cap'}${r.openHolds > 0 ? ` (+${r.openHolds} in checkout)` : ''}`;

  return (
    <div>
      <PortalHeader showCredits={false} />

      <div className="container" style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingTop: '16px', paddingBottom: 0 }}>
        <Link to="/admin" className="back-link">Back</Link>
        <h1 style={{ margin: 0, fontSize: '1.2rem' }}>Promo Codes</h1>
      </div>

      <div className="container">
        <h2 className="section-title">Create a code</h2>
        <p style={{ color: 'var(--gray-400)', fontSize: '0.85rem', marginTop: 0 }}>
          A code is a percent off, typed by the client on the Buy Credits confirm step. If the client already has a
          standing discount, the better of the two applies (never both). To change a code, switch it off and create a new one.
        </p>

        <form onSubmit={handleCreate} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Who can use it</label>
            <select value={kind} onChange={e => setKind(e.target.value as 'shared' | 'single_client')}>
              <option value="shared">Any client (shared code)</option>
              <option value="single_client">One client only (one use)</option>
            </select>
          </div>

          {kind === 'single_client' && (
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Client</label>
              <select value={clientId} onChange={e => setClientId(e.target.value)}>
                <option value="">Pick a client...</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name || c.email || c.id}</option>)}
              </select>
            </div>
          )}

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Percent off (1-90)</label>
            <input type="number" inputMode="numeric" min="1" max="90" step="1" value={percentOff}
              onChange={e => setPercentOff(e.target.value)} placeholder="15" />
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Applies to</label>
            <select value={scope} onChange={e => setScope(e.target.value as 'credits' | 'platform' | 'both')}>
              <option value="both">Credit packs + platform fee</option>
              <option value="credits">Credit packs only</option>
              <option value="platform">Platform fee only</option>
            </select>
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Code (blank = make one for me)</label>
            <input type="text" value={code} maxLength={24} autoComplete="off" spellCheck={false}
              onChange={e => setCode(e.target.value.toUpperCase())} placeholder="e.g. FALL15" />
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Start date (optional)</label>
            <input type="date" value={startsOn} onChange={e => setStartsOn(e.target.value)} />
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>{kind === 'shared' ? 'Cut-off date (required)' : 'Cut-off date (optional)'}</label>
            <input type="date" value={endsOn} onChange={e => setEndsOn(e.target.value)} />
          </div>

          {kind === 'shared' && (
            <>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Uses per client</label>
                <input type="number" inputMode="numeric" min="1" step="1" value={maxPerClient} onChange={e => setMaxPerClient(e.target.value)} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Total uses, all clients (blank = no cap)</label>
                <input type="number" inputMode="numeric" min="1" step="1" value={maxTotal} onChange={e => setMaxTotal(e.target.value)} placeholder="no cap" />
              </div>
            </>
          )}

          <div className="form-group" style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
            <label>Note (only you see this)</label>
            <input type="text" value={note} maxLength={500} onChange={e => setNote(e.target.value)} placeholder="Why this code exists" />
          </div>

          <div style={{ gridColumn: '1 / -1', color: 'var(--gray-400)', fontSize: '0.8rem' }}>
            {kind === 'single_client'
              ? 'One client, one use. Works on their account only.'
              : 'Dates are Central time: a code works from the start of the start date to the end of the cut-off date. "1" and blank = once per client. "1" and "1" = one use in total, first come first served.'}
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Creating...' : 'Create code'}
            </button>
          </div>
        </form>

        {formError && (
          <div style={{ marginBottom: '16px', padding: '12px', borderRadius: '8px', background: 'var(--red-bg)', color: 'var(--red)' }}>{formError}</div>
        )}
        {created && (
          <div style={{
            marginBottom: '24px', padding: '14px', borderRadius: '8px', background: 'var(--green-bg)', color: 'var(--green)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap',
          }}>
            <span>Code created: <strong style={{ fontSize: '1.1rem', letterSpacing: '0.05em' }}>{created}</strong></span>
            <button type="button" className="btn btn-secondary" onClick={copyCreated}>{copied ? 'Copied' : 'Copy code'}</button>
          </div>
        )}

        <h2 className="section-title">All codes</h2>
        {rowError && (
          <div style={{ marginBottom: '12px', padding: '12px', borderRadius: '8px', background: 'var(--red-bg)', color: 'var(--red)' }}>{rowError}</div>
        )}
        {listLoading ? (
          <div className="loading">Loading codes...</div>
        ) : listError ? (
          <div style={{ padding: '12px', borderRadius: '8px', background: 'var(--red-bg)', color: 'var(--red)' }}>
            {listError} <button type="button" className="btn btn-secondary" onClick={loadCodes} style={{ marginLeft: '8px' }}>Retry</button>
          </div>
        ) : codes.length === 0 ? (
          <div className="loading">No promo codes yet</div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Status</th>
                  <th>Discount</th>
                  <th>Who</th>
                  <th>Dates (Central)</th>
                  <th>Used</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {codes.map(r => (
                  <tr key={r.code}>
                    <td>
                      <div style={{ fontWeight: 700, letterSpacing: '0.04em' }}>{r.code}</div>
                      {r.note && <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>{r.note}</div>}
                    </td>
                    <td style={{ color: STATUS_COLOR[r.status], fontWeight: 600 }}>{STATUS_LABEL[r.status]}</td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{r.percentOff ?? '?'}% off</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>{SCOPE_LABEL[r.scope] || r.scope}</div>
                    </td>
                    <td style={{ fontSize: '0.85rem' }}>
                      {r.kind === 'single_client'
                        ? <Link to={`/admin/client/${r.clientId}`}>{clientName(r.clientId)}</Link>
                        : `Any client, ${r.maxPerClient ?? 1} each`}
                    </td>
                    <td style={{ fontSize: '0.85rem' }}>{r.startsOn || 'now'} to {r.endsOn || 'no end'}</td>
                    <td style={{ fontSize: '0.85rem' }}>{usedText(r)}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button type="button" className="btn btn-secondary" style={{ padding: '6px 10px', fontSize: '0.8rem', marginRight: '6px' }}
                        onClick={() => handleViewUses(r)}>
                        {openCode === r.code ? 'Hide uses' : 'Uses'}
                      </button>
                      {r.status !== 'broken' && (
                        <button type="button" className={r.active ? 'btn btn-secondary' : 'btn btn-primary'}
                          style={{ padding: '6px 10px', fontSize: '0.8rem' }} disabled={busyCode !== null}
                          onClick={() => handleToggle(r)}>
                          {busyCode === r.code ? '...' : r.active ? `Switch off ${r.code}` : `Switch on ${r.code}`}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {openCode && (
          <div style={{ marginTop: '16px' }}>
            <h2 className="section-title">Uses of {openCode}</h2>
            {redemptions === null ? (
              <div className="loading">Loading...</div>
            ) : redemptions.length === 0 ? (
              <div className="loading">Nobody has used this code yet</div>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr><th>Client</th><th>Status</th><th>Item</th><th>List</th><th>Discount</th><th>Paid</th><th>When</th></tr>
                  </thead>
                  <tbody>
                    {redemptions.map(x => (
                      <tr key={x.id}>
                        <td><Link to={`/admin/client/${x.clientId}`}>{clientName(x.clientId)}</Link></td>
                        <td style={{ fontWeight: 600, color: x.status === 'redeemed' ? '#10b981' : 'var(--gray-400)' }}>
                          {x.status === 'redeemed' ? 'Paid' : x.status === 'released' ? 'Gave it back' : x.lapsed ? 'Abandoned' : 'In checkout'}
                          {x.overRedeemed && <span style={{ color: '#dc2626' }}> (over limit)</span>}
                        </td>
                        <td>{x.tierId}</td>
                        <td>{usd(x.listUsd)}</td>
                        <td>{usd(x.discountUsd)}</td>
                        <td>{usd(x.netUsd)}</td>
                        <td style={{ fontSize: '0.8rem' }}>{when(x.redeemedAtMs ?? x.reservedAtMs)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
